import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import classNames from "classnames";
import _ from "lodash";
import { Fragment } from "react";
import { ChartResponse } from "../../../../models/chart";

type ChartSearchDialogProps = {
  title: string;
  searchPlaceholderText: string;
  search: () => void;
  searchResults: any[];
  query: string;
  year: string;
  selectedChart: ChartResponse;
  setSelectedChart: (selectedChart: ChartResponse) => void;
  importMarks: () => void;
  setQuery: (value: string) => void;
  setYear: (value: string) => void;
  isOpen: boolean;
  toggle: () => void;
};

const ChartSearchDialog = ({
  title,
  isOpen,
  toggle,
  searchPlaceholderText,
  searchResults,
  setQuery,
  setYear,
  year,
  selectedChart,
  setSelectedChart,
  importMarks,
}: ChartSearchDialogProps) => {
  const formatDate = (date: string) => {
    const dateObj = new Date(date);
    const year = new Date(dateObj).getFullYear();
    const month = dateObj
      .toLocaleString("en-US", { month: "short" })
      .toUpperCase();
    return `${month} ${year}`;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        open={isOpen}
        onClose={() => toggle()}
        className="relative z-[100]"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="flex flex-col w-1/3 p-8 mx-auto bg-white rounded h-1/2">
              <div className="flex flex-row justify-end w-full mb-4 ml-6 -mt-6 -pr-4">
                <XMarkIcon
                  height={30}
                  width={30}
                  onClick={() => toggle()}
                  className="cursor-pointer"
                />
              </div>
              <Dialog.Title className="mb-4 text-2xl font-semibold text-center uppercase text-primary-blue">
                {title}
              </Dialog.Title>
              <p className="mx-4 mb-6 text-center text-gray-500">
                Search for a similar ballot in order to import marks to the new
                ballot.
              </p>
              <div className="flex flex-row justify-between w-full">
                <input
                  type="text"
                  placeholder={searchPlaceholderText}
                  onChange={(e) => setQuery(e.target.value || "")}
                  className="w-1/2 p-2 mt-2 mb-6 mr-4 border rounded-md border-primary-blue focus:outline-none"
                />
                <select
                  defaultValue="Year"
                  name="year"
                  id="year"
                  className={classNames(
                    "w-1/2 p-2 mt-2 mb-6 border rounded-md border-primary-blue focus:outline-none",
                    { "text-gray-400": !year }
                  )}
                  onChange={(e) => setYear(e.target.value)}
                >
                  <option disabled value="Year">
                    Year
                  </option>
                  {_.range(new Date().getFullYear(), 1999, -1).map(
                    (year: number) => (
                      <option value={year} key={`year-${year}`}>
                        {year}
                      </option>
                    )
                  )}
                </select>
              </div>
              <div className="flex-grow overflow-y-scroll justify-items-center gap-y-2 h-1/2">
                {searchResults &&
                  searchResults.map((result, i) => {
                    return (
                      <div
                        key={i}
                        className={classNames(
                          "flex flex-row p-3 m-1 rounded-md cursor-pointer hover:bg-secondary-lightblue focus:bg-secondary-lightblue",
                          result.id === selectedChart.id
                            ? "bg-blue-200"
                            : "bg-gray-100"
                        )}
                        onClick={() => setSelectedChart(result)}
                      >
                        <p className="font-semibold">
                          {result.county} | precinct {result.precinct} |
                        </p>
                        <p className="ml-1 font-semibold text-gray-500">
                          {formatDate(result.electionDate.split("T")[0])}
                        </p>
                      </div>
                    );
                  })}
              </div>
              <div className="flex flex-row justify-end w-full mt-6">
                <button
                  className="uppercase tracking-[0.07rem] justify-center items-center px-6 py-3 text-white bg-primary-blue text-base font-semibold rounded-md shadow-sm hover:bg-white border hover:text-primary-blue hover:border-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:cursor-not-allowed disabled:border-gray-500 disabled:text-gray-500 disabled:bg-white diabled:bg-transparent"
                  onClick={() => importMarks()}
                  disabled={!selectedChart}
                >
                  import marks
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ChartSearchDialog;
